import { useEffect, useState, useCallback } from "react"
import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"
import { readResolvedArray, recordValue, shorthandId } from "./property-value-utils"
import { liftV4Props } from "./element-renderer"

function treeElement(parentId: string, raw: unknown, index: number): CogoElement {
  const item = recordValue(raw)
  const id = String(item.id ?? shorthandId(parentId, "items", index))
  const rawChildren = Array.isArray(item.children)
    ? item.children
    : Array.isArray(item.items)
      ? item.items
      : []
  return {
    ...item,
    id,
    type: String(item.type ?? "tree-item"),
    text: item.label ?? item.text ?? item.name,
    children: rawChildren.map((child, childIndex) => treeElement(id, child, childIndex)),
  } as unknown as CogoElement
}

function resolveAuthoredValue(
  value: unknown,
  resolveSymbol: (value: string) => unknown,
): unknown {
  return typeof value === "string" ? resolveSymbol(value) : value
}

function expandedNodeIds(value: unknown): string[] {
  return Array.isArray(value) ? value.map(String) : []
}

function selectedNodeId(value: unknown): string | null {
  return value === undefined || value === null || value === ""
    ? null
    : String(value)
}

function TreeNode({
  node,
  depth,
  expanded,
  selectedNode,
  onActivate,
}: {
  node: CogoElement
  depth: number
  expanded: Set<string>
  selectedNode: string | null
  onActivate: (id: string, hasChildren: boolean) => void
}) {
  // JITRULE Task 2: recursion source — items reaching TreeNode may come from
  // explicit `element.children` (never lifted upstream) at ANY depth, since
  // each recursive call re-enters here. Lifting once at this single point
  // self-heals every depth without the caller needing to pre-lift the whole
  // subtree.
  node = liftV4Props(node)
  const hasChildren = Boolean(node.children && node.children.length > 0)
  const isExpanded = expanded.has(node.id)
  const isSelected = selectedNode === node.id

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className={cn(
          "cn-tree-node-button flex flex-row items-center gap-1.5 px-1 py-0.5 rounded-md bg-transparent border-none cursor-pointer text-left hover:bg-muted transition-colors",
          isSelected && "bg-muted",
        )}
        style={{ paddingLeft: `${depth * (node.indentSize ?? 16)}px` }}
        onClick={() => onActivate(node.id, hasChildren)}
        role="treeitem"
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-selected={isSelected}
        data-node-id={node.id}
        data-selected={isSelected ? "true" : "false"}
        aria-label={
          hasChildren
            ? `${isExpanded ? "Collapse" : "Expand"} ${node.text ?? node.id}`
            : node.text ?? node.id
        }
      >
        {hasChildren ? (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className={cn(
              "text-muted-foreground transition-transform duration-150",
              isExpanded && "rotate-90",
            )}
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        ) : (
          <span className="w-4" />
        )}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill={hasChildren ? "currentColor" : "none"}
          stroke={hasChildren ? "currentColor" : "currentColor"}
          strokeWidth="2"
          className={hasChildren ? (node.folderColorClass ?? "text-yellow-500") : "text-muted-foreground"}
        >
          {hasChildren ? (
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
          ) : (
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z M14 2v6h6" />
          )}
        </svg>
        <span className="text-sm text-foreground">{node.text ?? node.id}</span>
      </button>
      {hasChildren &&
        isExpanded &&
        node.children!.map((child) => (
          <TreeNode
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            selectedNode={selectedNode}
            onActivate={onActivate}
          />
        ))}
    </div>
  )
}

export function TreeRenderer({ element }: RendererProps) {
  const { dispatchEvent, resolveSymbol } = useRenderContext()
  const elementRecord = element as unknown as Record<string, unknown>
  const authoredExpandedNodes = expandedNodeIds(
    resolveAuthoredValue(elementRecord.expandedNodes, resolveSymbol),
  )
  const authoredExpandedSignature = JSON.stringify(authoredExpandedNodes)
  const authoredSelectedNode = selectedNodeId(
    resolveAuthoredValue(elementRecord.selectedNode, resolveSymbol),
  )
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(authoredExpandedNodes),
  )
  const [selectedNode, setSelectedNode] = useState<string | null>(
    authoredSelectedNode,
  )

  useEffect(() => {
    const nextExpandedNodes = JSON.parse(authoredExpandedSignature) as string[]
    setExpanded(new Set(nextExpandedNodes))
    setSelectedNode(authoredSelectedNode)
    dispatchEvent(element.id, "stateChange", {
      expandedNodes: nextExpandedNodes,
      selectedNode: authoredSelectedNode,
      node: authoredSelectedNode,
      value: authoredSelectedNode,
    })
  }, [authoredExpandedSignature, authoredSelectedNode, element.id, dispatchEvent])

  const handleActivate = useCallback(
    (id: string, hasChildren: boolean) => {
      const next = new Set(expanded)
      if (hasChildren) {
        if (next.has(id)) {
          next.delete(id)
        } else {
          next.add(id)
        }
        setExpanded(next)
      }
      setSelectedNode(id)
      const nextExpandedNodes = [...next]
      dispatchEvent(element.id, "tap", {
        toggled: hasChildren ? id : undefined,
        expandedNodes: nextExpandedNodes,
        selectedNode: id,
        node: id,
        value: id,
      })
    },
    [expanded, element.id, dispatchEvent],
  )

  const explicitItems = element.children ?? []
  const items = explicitItems.length > 0
    ? explicitItems
    : readResolvedArray(element, "items", resolveSymbol).map((item, index) => treeElement(element.id, item, index))

  return (
    <div
      className={cn("cn-tree flex flex-col", element.className)}
      data-testid={`tree-${element.id}`}
      role="tree"
      data-expanded-nodes={JSON.stringify([...expanded])}
      data-selected-node={selectedNode ?? undefined}
    >
      {items.map((item) => (
        <TreeNode
          key={item.id}
          node={item}
          depth={0}
          expanded={expanded}
          selectedNode={selectedNode}
          onActivate={handleActivate}
        />
      ))}
    </div>
  )
}
